// /app/frontend/src/pages/seo/FamilyBuildingHandbookPage.jsx
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildArticleSchema,
  buildBreadcrumbSchema,
  ReportHero,
  ReportSection,
  AboutThisReport,
  PullQuote,
  BottomCTA,
} from './SEOUtils';

const URL_PATH = '/family-building-handbook';
const PUBLISHED_ISO = '2026-02-15';
const PUBLISHED_HUMAN = 'February 2026';

export default function FamilyBuildingHandbookPage() {
  const schemas = [
    organizationSchema,
    buildArticleSchema({
      headline: 'The Family Building Handbook',
      description:
        'A guide to building strong relationships, healthy families, communication systems, parenting, and long-term partnership.',
      urlPath: URL_PATH,
      datePublishedISO: PUBLISHED_ISO,
    }),
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Reports', url: '/reports' },
      { name: 'The Family Building Handbook', url: URL_PATH },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="The Family Building Handbook | UnityLove"
        description="A guide to building strong relationships, healthy families, communication systems, parenting, and long-term partnership."
        canonical={URL_PATH}
        schemas={schemas}
      />

      <ReportHero
        eyebrow="UnityLove Handbook"
        dateline={`Published ${PUBLISHED_HUMAN}`}
        headline="The Family Building Handbook"
        dek="A practical, structure-agnostic guide for couples and families serious about building something that lasts."
      />

      <article className="max-w-3xl mx-auto px-4 py-12">
        <ReportSection n={1} title="Defining Your Family Vision">
          <p>
            Every family that lasts shares one practice: the adults inside it can articulate the same answer to the question, &ldquo;what are we building?&rdquo; Vision is not a wedding-day declaration. It is a working document — written together, revisited yearly, allowed to evolve as the family does.
          </p>
          <p>The vision conversation covers:</p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li>Long-term destination: 10, 20, 30 years out</li>
            <li>Children: whether, when, how many, parenting philosophy</li>
            <li>Geography: where you build, whether you stay or move</li>
            <li>Money: how you treat earning, spending, saving, generational wealth</li>
            <li>Faith or values framing: the spiritual or ethical foundation underneath</li>
            <li>Community: the role of extended family, friends, broader networks</li>
          </ul>
        </ReportSection>

        <ReportSection n={2} title="Choosing Shared Values">
          <p>
            Couples often discover after a few years together that they share affection but not values. Values are not personality traits; they are the deeper convictions that shape what you each refuse to give up. Naming them explicitly — before friction reveals them — saves families from drift.
          </p>
          <p>Values worth surfacing early:</p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li>What does honesty mean to you, including its uncomfortable forms?</li>
            <li>How do you treat family of origin and obligations to them?</li>
            <li>How is money treated — accumulation, generosity, scarcity, security?</li>
            <li>What role does faith or spiritual practice play?</li>
            <li>How is conflict handled — surfaced or avoided, in-the-moment or scheduled?</li>
          </ul>
        </ReportSection>

        <ReportSection n={3} title="Communication Systems">
          <p>
            The healthiest families do not communicate well by accident; they communicate well by design. Specifically, they build communication infrastructure:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li>Weekly check-ins: structured, brief, recurring — not reactive</li>
            <li>A &ldquo;name the feeling early&rdquo; rule that prevents resentment buildup</li>
            <li>An agreement on how big issues get raised — calmly, in the right context, at the right time</li>
            <li>An understanding that not every conversation needs resolution in the moment</li>
          </ul>
          <PullQuote>
            The relationships that thrive are not the ones without conflict. They are the ones where conflict is allowed to be named.
          </PullQuote>
        </ReportSection>

        <ReportSection n={4} title="Financial Alignment">
          <p>
            Money is the most underestimated source of family stress. Families avoid talking about it until something forces them to — and the conversation is then far more charged than it needed to be. The opposite move: build financial transparency as a routine practice.
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li>Quarterly money meetings — calm, scheduled, not crisis-driven</li>
            <li>Shared visibility into income, expenses, debts, savings</li>
            <li>Explicit decisions on shared vs. individual accounts</li>
            <li>Agreement on big spending thresholds before they become disputes</li>
            <li>Conversations about generational wealth, not just immediate spending</li>
          </ul>
        </ReportSection>

        <ReportSection n={5} title="Parenting & Children">
          <p>
            Children deserve adults who are aligned. Aligned parenting does not mean identical parenting — but it does mean kids cannot get one answer from one adult and a contradictory answer from another, especially on the things that matter. Worth aligning on:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li>Discipline philosophy and what consequences look like</li>
            <li>Education priorities</li>
            <li>Faith or values transmission</li>
            <li>Screens, media, social technology</li>
            <li>How the family is described to outsiders — especially in non-traditional structures</li>
            <li>The role of extended family, especially grandparents</li>
          </ul>
        </ReportSection>

        <ReportSection n={6} title="Conflict Resolution">
          <p>
            Every family has conflict. The healthy ones have a way through it. The patterns that work:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li>Name the conflict early, before it gathers weight</li>
            <li>Bring it directly to the person involved — never through a third party</li>
            <li>Separate the issue from the person; criticize behavior, not character</li>
            <li>Apologize specifically when wrong; vague apologies repair nothing</li>
            <li>Know when to pause — not every conversation needs to be resolved tonight</li>
            <li>Use outside help (therapist, mediator, trusted elder) when stuck</li>
          </ul>
        </ReportSection>

        <ReportSection n={7} title="Family Culture">
          <p>
            Every family has a culture — the unspoken set of rituals, language, humor, and habits that define what it feels like to be inside. Strong families build their culture intentionally. Sunday meals. A specific way of greeting each other. Family meetings. Vacation traditions. Small daily rituals that say: this is who we are.
          </p>
        </ReportSection>

        <ReportSection n={8} title="Long-Term Planning">
          <p>
            Beyond the next year, the families that thrive plan for the decades. Retirement, estate, generational wealth, health and aging, the future of children, what happens when adults pass. None of this is morbid; it is the work of love over a long horizon. The conversations are easier had calmly, in advance, than reactively during a crisis.
          </p>
        </ReportSection>

        <ReportSection n={9} title="Legacy Building">
          <p>
            What is a family for, ultimately? Most families that have thought about this answer some version of: to bring more love and care into the world than they found there. Legacy is not just money or property or even children. It is the way the family changes the people who pass through it — and the way that change ripples outward.
          </p>
          <p>
            Building family is the longest game most of us will ever play. The handbook does not end here; it is rewritten in every generation.
          </p>
        </ReportSection>

        <AboutThisReport
          published={PUBLISHED_HUMAN}
          body="This handbook draws on observations across the UnityLove community, public research on relationship and family dynamics, and the experiences of families across the African diaspora. It is intended as a starting framework for conversation, not as legal, financial, or therapeutic advice."
        />

        <section className="mt-12">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">Read Next</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/poly-family-planning" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Poly Family Planning</p>
              <p className="text-sm text-slate-600 mt-1">Practical planning across plural structures.</p>
            </a>
            <a href="/building-a-polygamous-family" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Building A Polygamous Family</p>
              <p className="text-sm text-slate-600 mt-1">Values and foundation.</p>
            </a>
            <a href="/dating-as-a-couple" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Dating As A Couple</p>
              <p className="text-sm text-slate-600 mt-1">Co-dating logistics and communication.</p>
            </a>
            <a href="/dating-app-for-intentional-relationships" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Dating App For Intentional Relationships</p>
              <p className="text-sm text-slate-600 mt-1">Why slow dating works better.</p>
            </a>
          </div>
        </section>
      </article>

      <BottomCTA />
    </div>
  );
}
